import React, { useState, useEffect } from 'react';

const DEFAULT_VPN_SERVER = {
  type: 'l2tp',
  name: 'l2tp-server',
  ip_pool: 'vpn-pool',
  local_address: '10.10.10.1',
  remote_address: '10.10.10.100-10.10.10.200',
  dns_server: '8.8.8.8',
  mtu: 1450,
  mru: 1450,
  use_encryption: true,
  require_peer_encryption: false,
  keepalive_timeout: '30s',
  comment: 'VPN Server',
};

const DEFAULT_VPN_USER = {
  name: 'vpnuser',
  password: 'vpn1234',
  service: 'l2tp',
  profile: 'default',
};

export default function StepVPN({ data, onChange, onNext, onBack, interfaceNames }) {
  const [server, setServer] = useState(data?.server || DEFAULT_VPN_SERVER);
  const [users, setUsers] = useState(data?.users || [{ ...DEFAULT_VPN_USER }]);
  const [enabled, setEnabled] = useState(data?.enabled ?? true);

  useEffect(() => {
    if (data) {
      setServer(data.server || DEFAULT_VPN_SERVER);
      setUsers(data.users || [{ ...DEFAULT_VPN_USER }]);
      setEnabled(data.enabled ?? true);
    }
  }, [data]);

  const save = (updated) => {
    onChange(updated);
  };

  const updateServer = (key, value) => {
    const updated = { ...server, [key]: value };
    setServer(updated);
    save({ server: updated, users, enabled });
  };

  const addUser = () => {
    const updated = [...users, { ...DEFAULT_VPN_USER, name: 'vpnuser' + (users.length + 1) }];
    setUsers(updated);
    save({ server, users: updated, enabled });
  };

  const removeUser = (index) => {
    if (users.length <= 1) return;
    const updated = users.filter((_, i) => i !== index);
    setUsers(updated);
    save({ server, users: updated, enabled });
  };

  const updateUser = (index, key, value) => {
    const updated = [...users];
    updated[index] = { ...updated[index], [key]: value };
    setUsers(updated);
    save({ server, users: updated, enabled });
  };

  const vpnTypes = [
    { value: 'pptp', label: 'PPTP', icon: '🔗', desc: 'เก่า, ไม่ปลอดภัย, ง่าย' },
    { value: 'l2tp', label: 'L2TP/IPsec', icon: '🛡️', desc: 'ปลอดภัยปานกลาง, ใช้บ่อย' },
    { value: 'sstp', label: 'SSTP', icon: '🔒', desc: 'ปลอดภัยสูง, ผ่าน HTTPS' },
    { value: 'ovpn', label: 'OpenVPN', icon: '🌐', desc: 'ปลอดภัยสูงสุด, ยืดหยุ่น' },
  ];

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">🔐 VPN Server</h2>
        <p className="text-gray-500 dark:text-gray-400">ตั้งค่า VPN Server ให้ลูกข่ายเชื่อมต่อจากภายนอกได้อย่างปลอดภัย</p>
      </div>

      {/* Enable/Disable */}
      <label className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 cursor-pointer mb-4">
        <div>
          <span className="text-sm font-semibold text-gray-900 dark:text-white">เปิดใช้งาน VPN Server</span>
          <p className="text-xs text-gray-400 mt-0.5">สร้าง VPN Server สำหรับลูกข่ายเชื่อมต่อ</p>
        </div>
        <input type="checkbox" checked={enabled} onChange={e => { setEnabled(e.target.checked); save({ server, users, enabled: e.target.checked }); }} className="w-5 h-5 text-violet-600 rounded" />
      </label>

      {enabled && (
        <>
          {/* VPN Type */}
          <div className="mb-4 p-4 bg-gradient-to-br from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl border-2 border-gray-200 dark:border-gray-600 shadow-sm">
            <h3 className="font-bold text-gray-900 dark:text-white mb-3">ประเภท VPN Server</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {vpnTypes.map(vt => (
                <button
                  key={vt.value}
                  onClick={() => updateServer('type', vt.value)}
                  className={`p-3 rounded-xl border-2 text-left text-sm transition-all ${
                    server.type === vt.value
                      ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  <div className="text-lg mb-0.5">{vt.icon}</div>
                  <div className="font-bold">{vt.label}</div>
                  <div className="text-[10px] opacity-70">{vt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Server Settings */}
          <div className="mb-4 p-4 bg-gradient-to-br from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl border-2 border-gray-200 dark:border-gray-600 shadow-sm">
            <h3 className="font-bold text-gray-900 dark:text-white mb-3">⚙️ ตั้งค่า VPN Server</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label">IP Pool (ช่วง IP สำหรับ VPN)</label>
                <input type="text" value={server.remote_address} onChange={e => updateServer('remote_address', e.target.value)} className="input-field" placeholder="10.10.10.100-10.10.10.200" />
              </div>
              <div>
                <label className="label">Local Address (IP ของ VPN Server)</label>
                <input type="text" value={server.local_address} onChange={e => updateServer('local_address', e.target.value)} className="input-field" placeholder="10.10.10.1" />
              </div>
              <div>
                <label className="label">DNS Server สำหรับ VPN</label>
                <input type="text" value={server.dns_server} onChange={e => updateServer('dns_server', e.target.value)} className="input-field" placeholder="8.8.8.8" />
              </div>
              <div>
                <label className="label">MTU / MRU</label>
                <input type="number" value={server.mtu} onChange={e => updateServer('mtu', parseInt(e.target.value) || 1450)} className="input-field" />
              </div>
              <div>
                <label className="label">IPsec Pre-Shared Key (สำหรับ L2TP)</label>
                <input type="password" value={server.ipsec_secret || ''} onChange={e => updateServer('ipsec_secret', e.target.value)} className="input-field" placeholder="my-secret-key" />
              </div>
              <div>
                <label className="label">Keepalive Timeout</label>
                <select value={server.keepalive_timeout} onChange={e => updateServer('keepalive_timeout', e.target.value)} className="input-field">
                  <option value="10s">10 วินาที</option>
                  <option value="30s">30 วินาที</option>
                  <option value="1m">1 นาที</option>
                  <option value="5m">5 นาที</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <label className="flex items-center gap-2 p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 cursor-pointer">
                <input type="checkbox" checked={server.use_encryption} onChange={e => updateServer('use_encryption', e.target.checked)} className="w-4 h-4 text-violet-600 rounded" />
                <span className="text-sm text-gray-700 dark:text-gray-300">ใช้ Encryption</span>
              </label>
              <label className="flex items-center gap-2 p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 cursor-pointer">
                <input type="checkbox" checked={server.require_peer_encryption} onChange={e => updateServer('require_peer_encryption', e.target.checked)} className="w-4 h-4 text-violet-600 rounded" />
                <span className="text-sm text-gray-700 dark:text-gray-300">บังคับ Encryption</span>
              </label>
            </div>
          </div>

          {/* VPN Users */}
          <div className="mb-4 p-4 bg-gradient-to-br from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl border-2 border-gray-200 dark:border-gray-600 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-900 dark:text-white">👤 ผู้ใช้ VPN ({users.length} คน)</h3>
              <button onClick={addUser} className="btn-primary text-sm">+ เพิ่มผู้ใช้</button>
            </div>
            <div className="space-y-3">
              {users.map((user, i) => (
                <div key={i} className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">ผู้ใช้ #{i + 1}</span>
                    <button onClick={() => removeUser(i)} className="btn-danger" disabled={users.length <= 1}>ลบ</button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input type="text" value={user.name} onChange={e => updateUser(i, 'name', e.target.value)} className="input-field text-sm" placeholder="Username" />
                    <input type="text" value={user.password} onChange={e => updateUser(i, 'password', e.target.value)} className="input-field text-sm" placeholder="Password" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="p-3 bg-violet-50 dark:bg-violet-900/10 border border-violet-200 dark:border-violet-800/50 rounded-lg">
            <p className="text-xs text-violet-700 dark:text-violet-300">
              <strong>💡 คำแนะนำ:</strong> L2TP/IPsec เหมาะสมที่สุดสำหรับการใช้งานทั่วไป ควรเปลี่ยน IPsec Pre-Shared Key เป็นรหัสที่ซับซ้อน
            </p>
          </div>
        </>
      )}

      <div className="flex justify-between mt-6">
        <button onClick={onBack} className="btn-secondary">← ย้อนกลับ</button>
        <button onClick={onNext} className="btn-primary">ถัดไป ({users.length} ผู้ใช้)</button>
      </div>
    </div>
  );
}
